import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const faqs = [
  { q: "Hur fungerar dispensern?", a: "Dispensern ställs ut i önskad miljö och laddas med brustabletter. Sen är den redo – man drar helt enkelt ut sin brustablett direkt från dispensern." },
  { q: "Kan jag testa innan jag köper?", a: "Absolut! Kontakta oss så skickar vi ett provpaket med alla fyra smaker." },
  { q: "Hur ofta behöver man fylla på?", a: "Det beror på antal medarbetare. En tub ger 20 drycker. Med prenumeration skickar vi automatiskt." },
  { q: "Finns det bindningstid?", a: "Nej, inga bindningstider. Du kan pausa eller avsluta din prenumeration när som helst." },
  { q: "Vilka ingredienser innehåller tabletterna?", a: "Alla våra brustabletter är baserade på naturliga smaker och innehåller vitaminer och mineraler. Se produktsidan för fullständig innehållsförteckning." },
];

const RevealDiv = ({
  children,
  delay = 0,
  fromRight = false,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  fromRight?: boolean;
  className?: string;
}) => {
  const { ref, className: revealClass } = useReveal({ fromRight });
  return (
    <div ref={ref} className={`${revealClass} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const FadeUp = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`fade-up ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const formEl = e.currentTarget as HTMLFormElement;
    const fd = new FormData(formEl);
    const name = String(fd.get("name") ?? "").trim();
    const company = String(fd.get("company") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    const message = String(fd.get("message") ?? "").trim();

    const id = crypto.randomUUID();

    try {
      const { data, error } = await supabase.functions.invoke("submit-contact-form", {
        body: { submissionId: id, name, company, email, phone, message },
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error ?? error?.message ?? "Kunde inte skicka");
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Contact email dispatch failed", err);
      toast.error("Kunde inte skicka meddelandet. Försök igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "aQuadias",
    email: "info@aquadias.se",
    telephone: "+46707602770",
    url: "https://aquadias.se/",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Zenitvägen 67",
      postalCode: "175 65",
      addressLocality: "Järfälla",
      addressCountry: "SE",
    },
  };
  return (
    <Layout>
      <Seo
        title="Kontakt – Aquadias"
        description="Kontakta aQuadias för frågor, prov eller offert. Telefon, mejl och adress till vårt kontor i Järfälla."
        path="/kontakt"
        jsonLd={[faqLd, localBusinessLd]}
      />
      <section className="bg-gradient-to-b from-coral-500/20 via-cream-100 to-cream-50 py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16">
          <RevealDiv>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight mb-6">Kontakta oss</h1>
            <p className="text-lg text-muted-foreground mb-10">
              Nyfiken på aQuadias? Vill du beställa, samarbeta eller bara säga hej? Vi hör gärna av dig!
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-coral-500 mt-1" />
                <div>
                  <p className="font-medium text-foreground">E-post</p>
                  <p className="text-muted-foreground">info@aquadias.se</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-coral-500 mt-1" />
                <div>
                  <p className="font-medium text-foreground">Telefon</p>
                  <p className="text-muted-foreground">070-760 27 70</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-coral-500 mt-1" />
                <div>
                  <p className="font-medium text-foreground">Adress</p>
                  <p className="text-muted-foreground">Zenitvägen 67<br />175 65 Järfälla</p>
                </div>
              </div>
            </div>
          </RevealDiv>

          <RevealDiv fromRight delay={150} className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-10">
            {submitted ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-2xl font-semibold text-coral-600 mb-2">Tack!</p>
                  <p className="text-muted-foreground">Vi återkommer inom 24 timmar.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="text-2xl font-semibold text-foreground mb-2">Skicka meddelande</h2>
                <input name="name" type="text" placeholder="Ditt namn" required className="w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-coral-500 focus:ring-2 focus:ring-coral-400/20 transition-all" />
                <input name="company" type="text" placeholder="Företag" className="w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-coral-500 focus:ring-2 focus:ring-coral-400/20 transition-all" />
                <input name="email" type="email" placeholder="E-postadress" required className="w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-coral-500 focus:ring-2 focus:ring-coral-400/20 transition-all" />
                <input name="phone" type="tel" placeholder="Telefonnummer" className="w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-coral-500 focus:ring-2 focus:ring-coral-400/20 transition-all" />
                <textarea name="message" placeholder="Ditt meddelande" rows={4} required className="w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-coral-500 focus:ring-2 focus:ring-coral-400/20 transition-all resize-none" />
                <button type="submit" disabled={submitting} className="w-full bg-terracotta-500 text-white rounded-full py-3 font-medium hover:bg-terracotta-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? "Skickar..." : "Skicka"}
                </button>
              </form>
            )}
          </RevealDiv>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 py-20 md:py-28 bg-cream-50">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-foreground mb-12 text-center">Vanliga frågor</h2>
          </FadeUp>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FadeUp key={i} delay={i * 80}>
                <div className="bg-white rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex justify-between items-center p-5 text-left"
                  >
                    <span className="font-medium text-foreground pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
